import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  TableCell,
  TableRow,
  WidthType,
  Table,
  Footer,
  SimpleField,
  ImageRun,
  HorizontalPositionRelativeFrom,
  VerticalPositionRelativeFrom,
  TextWrappingType,
} from "docx";
import { saveAs } from "file-saver";
import { IproductRegistration } from "../models/epcCustomer.model";
import { formatToDDMMYYYY } from "./formatDateUtils";
import toast from "react-hot-toast";
import stampUrl from "../assets/signs/stamp-1.png";

export const generateModelDraftAgreement = async (
  setGeneratingDoc: (value: boolean) => void,
  productRegistration: IproductRegistration,
) => {
  setGeneratingDoc(true);
  const stampBuffer = await fetch(stampUrl).then((res) => res.arrayBuffer());
  try {
    const doc = new Document({
      styles: {
        default: {
          document: {
            run: {
              font: "Calibri",
              size: 24,
            },
          },
          heading1: {
            run: {
              font: "Calibri",
            },
          },
          heading2: {
            run: {
              font: "Calibri",
            },
          },
          heading3: {
            run: {
              font: "Calibri",
            },
          },
        },
      },
      sections: [
        {
          children: [
            // Title
            new Paragraph({
              children: [
                new TextRun({
                  text: "Annexure 2",
                  bold: true,
                  color: "000000",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Model Draft Agreement between Consumer & Vendor for installation of grid connected rooftop solar (RTS) project under PM -- Surya Ghar: Muft Bijli Yojana",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "This agreement is executed on --------(Day)--------(Month)--------(Year)  for design, supply, installation, commissioning and 5-year comprehensive maintenance of RTS project/system along with warranty under PM Surya Ghar: Muft Bijli Yojana",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // Between section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Between",
                  bold: true,
                  color: "000000",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `${productRegistration?.name}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: `  (Name of Consumer) having address at `,
                }),
                new TextRun({
                  text: `${productRegistration?.address}`,
                  bold: true,

                  underline: {},
                }),
                new TextRun({
                  text: "  (hereinafter referred to as first Party i.e. /consumer/consumer/purchaser /owner of system).",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // And section
            new Paragraph({
              children: [
                new TextRun({
                  text: "And",
                  bold: true,
                  color: "000000",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "FRELIT ENERGY PRIVATE LIMITED",
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " (Name of Vendor) having registered office at",
                }),
                new TextRun({
                  text: " 3B-20, Bhagya Laxmi Society, Beside Aton Gym, Near Darbar Chokdi, MANJALPUR VADODARA- 390011",
                  bold: true,
                }),
                new TextRun({
                  text: " (hereinafter referred to as second Party i.e. Vendor/ contractor/ System Integrator).",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // Whereas sections
            new Paragraph({
              children: [
                new TextRun({
                  text: "Whereas",
                  bold: true,
                  color: "000000",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "First Party wishes to install a Grid Connected Rooftop Solar Plant on the rooftop of the residential building of the Consumer under PM Surya Ghar: Muft Bijli Yojana.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "And whereas",
                  bold: true,
                  color: "000000",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Second Party has verified availability of appropriate roof and found it feasible to install a Grid Connected Roof Top Solar plant and that the second party is willing to design, supply, install, test, commission and carry out Operation & Maintenance of the Rooftop Solar plant for 5 year period",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "On this day, the First Party and Second Party agree to the following:",
                }),
              ],
              spacing: { after: 400 },
            }),

            // First Party responsibilities
            new Paragraph({
              children: [
                new TextRun({
                  text: "The First Party hereby undertakes to perform the following activities:",
                  underline: {},

                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Submission of online application at National Portal for installation of RTS project/system, Submission of application for net-metering and system inspection and upload of the relevant documents on the National Portal of the scheme",
              "Provide secure storage of the material of the RTS plant delivered at the premises till handover of the system.",
              "Provide access to the Roof Top during installation of the plant, operation & maintenance, testing of the plant and equipment and for meter reading from solar meter, inverter etc.",
              "Provide electricity during plant installation and water for cleaning of the panels.",
              "Report any malfunctioning of the plant to the Vendor during the warranty period.",
              "Pay the amount as per the payment schedule as mutually agreed with the vendor, including any additional amount to the second party for any additional work /customization required depending upon the building condition",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index + 1}.  ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 100, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            new Paragraph({
              text: "",
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // Second Party responsibilities
            new Paragraph({
              children: [
                new TextRun({
                  text: "The Second Party hereby undertakes to perform the following activities:",
                  underline: {},

                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "The Vendor must follow all the standards and safety guidelines prescribed under state regulations and technical standards prescribed by MNRE for RTS projects, failing which the vendor is liable for blacklisting from participation in the govt. project/ scheme and other penal actions in accordance with the law. The responsibility of supply, installation and commissioning of the rooftop solar project/system in complete compliance with MNRE scheme guidelines lies with the Vendor.",
              "Site Survey: Site visit, survey and development of detailed project report for installation of RTS system. This also includes, feasibility study of roof, strength of roof and shadow free area. If any additional work or customization is involved for the plant installation as per site condition and requirement of the consumer building, the Vendor shall prepare an estimate and can raise separate invoice including GST in addition to the amount towards standard plant cost. The consumer shall pay the amount for such additional work directly to the Vendor.",
              "Design & Engineering: Design of plant along with drawings and selection of components as per standard provided by the DISCOM/SERC/MNRE for best performance and safety of the plant.",
              "Module and Inverter: The solar modules, including the solar cells, should be manufactured in India. Both the solar modules and inverters shall conform to the relevant standards and specifications prescribed by MNRE. Any other requirement, viz. star labelling (solar modules), quality control orders and standards & labelling (inverters) etc., shall also be complied.",
              "Procurement & Supply: Procurement of complete system as per BIS/IS/IEC standard (whatever applicable) & safety guidelines for installation of rooftop solar plants. The supplied materials should comply with all MNRE standards for release of subsidy.",
              "Installation & Civil work: Complete civil work, structure work and electrical work (including drawings) following all the safety and relevant BIS standards.",
              "Documentation (Technical Catalogues/Warranty Certificates/BIS certificates/other test reports etc): All such documents shall be provided to the consumer for online uploading and submission of technical specifications, IEC/BIS report, Sr. Nos, Warranty card of Solar Panel & Inverter, Layout & Electrical SLD, Structure Design and Drawing, Cable and other detailed documents.",
              "Project completion report (PCR): Assisting the consumer in filling and uploading of signed documents (Consumer & Vendor) on the national portal.",
              "Warranty: System warranty certificates should be provided to the consumer. The complete system should be warranted for 5 years from the date of commissioning by DISCOM. Individual component warranty documents provided by the manufacturer shall be provided to the consumer and all possible assistance should be extended to the consumer for claiming the warranty from the manufacturer.",
              "NET meter & Grid Connectivity: Net meter supply/procurement, testing and approvals shall be in the scope of vendor. Grid connection of the plant shall be in the scope of the vendor.",
              "Testing and Commissioning: The vendor shall be present at the time of testing and commissioning by the DISCOM.",
              "Operation & Maintenance: Five (5) years Comprehensive Operation and Maintenance including overhauling, wear and tear and regular checking of healthiness of system at proper interval shall be in the scope of vendor. The vendor shall also educate the consumer on best practices for cleaning of the modules and system maintenance.",
              "Insurance: Any insurance cost pertaining to material transfer/storage before commissioning of the system shall be in the scope of the vendor.",
              "Applicable Standard: The system must meet the technical standards and specifications notified by MNRE. The vendor is solely responsible to supply component and service which meets the technical standards and specification prescribed by MNRE and State DISCOMs.",
              "Project/system cost & payment terms: The cost of the plant and payment schedule should be mutually discussed and decided between the vendor and consumer. The consumer may opt for milestone-based payment to the vendor and the same shall be included in the agreement.",
              "Dispute: In-case of any dispute between consumer and vendor (in supply/installation/maintenance of system or payment terms), both parties must settle the same mutually or as per law. MNRE/DISCOM shall not be liable for, and would not be a party to any dispute arising between vendor and consumer.",
              "Subsidy / Project Related Documents: Vendor must provide all the documents to consumer and help in uploading the same to National Portal for smooth release of subsidy.",
              "Performance of Plant: The Performance Ratio (PR) of Plant must be 75% at the time of commissioning of the project by DISCOM or its authorised agency. Vendor must provide (returnable basis) radiation sensor with valid calibration certificate of any NABL / International laboratory at the time of commissioning / testing of the plant. Vendor must maintain the PR of the plant till warranty of project i.e. 5 years from the date of commissioning.",
            ].map((item, index) => {
              const hasPrefix = item.includes(":");
              let prefix = "";
              let rest = item;

              if (hasPrefix) {
                const splitIndex = item.indexOf(":");
                prefix = item.substring(0, splitIndex + 1);
                rest = item.substring(splitIndex + 1);
              }

              const childRuns = [
                new TextRun({
                  text: `${index + 1}.  `,
                  bold: true,
                }),
                ...(hasPrefix
                  ? [
                      new TextRun({
                        text: prefix + " ",
                        bold: true,
                      }),
                    ]
                  : []),
                new TextRun({
                  text: rest.trim(),
                }),
              ];

              return new Paragraph({
                children: childRuns,
                alignment: AlignmentType.JUSTIFIED,
                spacing: { after: 400, line: 240 },
                indent: { left: 200 },
              });
            }),

            // Payment terms section
            new Paragraph({
              children: [
                new TextRun({
                  text: "19. Mutually Agreed Terms of Payment ...",
                  bold: true,
                }),
              ],
              spacing: { after: 400 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "First Party",

                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Name   :   ",

                  bold: true,
                }),
                new TextRun({
                  text: productRegistration?.name,
                }),
              ],
              spacing: { after: 100 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Address   :   ",

                  bold: true,
                }),
                new TextRun({
                  text: productRegistration?.address,
                }),
              ],
              spacing: { after: 100, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Sign",

                  bold: true,
                }),
              ],
              spacing: { after: 100 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Date",

                  bold: true,
                }),
              ],
              spacing: { after: 300 },
            }),

            // SECOND PARTY SECTION
            new Paragraph({
              children: [
                new TextRun({
                  text: "Second Party",

                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Name   :   ",

                  bold: true,
                }),
                new TextRun({
                  text: "FRELIT ENERGY PRIVATE LIMITED",
                }),
              ],
              spacing: { after: 100 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Address   :   ",

                  bold: true,
                }),
                new TextRun({
                  text: "3B-20, Bhagya Laxmi Society, Beside Aton Gym, Near Darbar Chokdi, MANJALPUR VADODARA- 390011",
                }),
              ],
              spacing: { after: 100, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Sign",
                  bold: true,
                }),
                new TextRun({
                  break: 2, // two line breaks
                }),
                new TextRun({
                  text: "Date",
                  bold: true,
                }),
                new ImageRun({
                  type: "png",
                  data: new Uint8Array(stampBuffer),
                  transformation: {
                    width: 185,
                    height: 170,
                  },
                  floating: {
                    horizontalPosition: {
                      relative: HorizontalPositionRelativeFrom.PAGE,
                      offset: 1_400_000, // move right
                    },
                    verticalPosition: {
                      relative: VerticalPositionRelativeFrom.PARAGRAPH,
                      offset: -100_00, // move up/down
                    },
                    wrap: {
                      type: TextWrappingType.NONE,
                    },
                    behindDocument: false,
                  },
                }),
              ],
            }),
          ],
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [new SimpleField("PAGE")],
                  alignment: AlignmentType.RIGHT,
                }),
              ],
            }),
          },
        },
      ],
    });

    const blob = await Packer.toBlob(doc);

    saveAs(blob, "Model_Draft_Agreement.docx");

    toast.success("Model Draft Agreement generated successfully!");
  } catch (error) {
    console.error("Error generating document:", error);
    toast.error("Failed to generate agreement document");
  } finally {
    setGeneratingDoc(false);
  }
};

export const generateProvisionalAgreement = async (
  setGeneratingDoc: (value: boolean) => void,
  productRegistration: IproductRegistration,
  inverterSize: string,
  companyType: "MGVCL" | "PGVCL" | "UGVCL" | "DGVCL" = "MGVCL",
) => {
  setGeneratingDoc(true);
  try {
    const doc = new Document({
      styles: {
        default: {
          document: {
            run: {
              font: "Calibri",
              size: 24,
            },
          },
        },
      },
      sections: [
        {
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: "(On Stamp Paper of Rs.300/-)",
                  bold: true,
                  underline: {},
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            //  TITLE
            new Paragraph({
              children: [
                new TextRun({
                  text: "Inter Connection agreement (Provisional)",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: "(Residential Projects Registered at GEDA / National Portal)",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),
            ...(companyType === "PGVCL"
              ? [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Project Registered under New RE Policy-2023",
                        bold: true,
                      }),
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: { after: 300 },
                  }),
                ]
              : []),

            // Header
            new Paragraph({
              children: [
                new TextRun({
                  text: "This Provisional Agreement is made and entered into at (location) ",
                }),
                new TextRun({
                  text: `${productRegistration?.area}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " on this (date) ___________________ day of (month)_______(year)_______ between the Consumer, by the name of ",
                }),
                new TextRun({
                  text: `${productRegistration?.name}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " Consumer Number ",
                }),
                new TextRun({
                  text: `${productRegistration?.consumerNumber}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " having premises at ",
                }),
                new TextRun({
                  text: `${productRegistration?.address}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: ' (hereinafter referred to as "Consumer" which expression shall include its permitted assigns and successors) as first party',
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // AND
            new Paragraph({
              children: [
                new TextRun({
                  text: "AND",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text:
                    companyType === "MGVCL"
                      ? "Madhya"
                      : companyType === "PGVCL"
                        ? "Paschim"
                        : companyType === "UGVCL"
                          ? "Uttar"
                          : "Dakshin",
                }),
                new TextRun({
                  text: ' Gujarat Vij Company Limited, a Company registered under the Companies Act 1956/2013 and functioning as the "Distribution Company" or "DISCOM" under the Electricity Act 2003 having its Head Office at, ',
                }),
                new TextRun({
                  text:
                    companyType === "MGVCL"
                      ? "Vadodara"
                      : companyType === "PGVCL"
                        ? "Rajkot"
                        : companyType === "UGVCL"
                          ? "Mehsana"
                          : '"Urja Sadan", Nana Varachha Road, Kapodara Char Rasta, Surat-395006',
                }),
                new TextRun({
                  text: ` (hereinafter referred to as "${companyType}" or "Distribution Licensee" or "DISCOM" which expression shall include its permitted assigns and successors) a Party of the Second Part.`,
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // AND WHEREAS 1
            new Paragraph({
              children: [
                new TextRun({
                  text: "AND WHEREAS",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,

              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The solar project of '",
                }),
                new TextRun({
                  text: `${productRegistration?.name}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: "' has been registered on GEDA / ",
                }),
                new TextRun({
                  text: "National Portal",
                  bold: true,
                }),
                new TextRun({
                  text: " on dtd.",
                }),
                new TextRun({
                  text: `${formatToDDMMYYYY(
                    productRegistration?.registeredAt,
                  )}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " to set up Photovoltaic (PV) based Solar Power Generating Plant (SPG) of ",
                }),
                new TextRun({
                  text: `${inverterSize} `,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " KW (AC) capacity at his/her/its premises in legal possession including any rooftop or terrace at ",
                }),
                new TextRun({
                  text: `${productRegistration?.address}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: ` connected with ${companyType}'s grid at _________ Voltage level for his/her/its own use within the same premises.`,
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // AND WHEREAS 2
            new Paragraph({
              children: [
                new TextRun({
                  text: "AND WHEREAS",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Government of Gujarat has declared Gujarat Renewable Energy Policy 2023 on 4.10.2023 operative for the control period from date of its notification (4.10.2023) to 30th September 2028.The RE Project installed and commissioned during the operative period shall become eligible for the benefits and incentives declared under the Policy, for the period of 25 years from the date of commissioning or for the life span of the RE Project System whichever is earlier.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // AND WHEREAS 3
            new Paragraph({
              children: [
                new TextRun({
                  text: "AND WHEREAS",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            ...(companyType === "DGVCL"
              ? [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `In order to facilitate commissioning of the solar projects pursuant to notification of New the Gujarat Renewable energy Policy - 2023 effective from 04.10.2023, ${companyType}  has agreed to sign this agreement on Provisional basis with Consumer in terms of provisions of the Gujarat RE Policy-2023 and its incorporation in the Gujarat Electricity Regulatory Commission (Net Metering Rooftop Solar PV Grid Interactive Systems Regulations) Notification No. 5 of 2016 and its subsequent amendments`,
                      }),
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 200, line: 360 },
                  }),

                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Under the option:",
                        bold: false,
                      }),
                    ],
                    spacing: { after: 200 },
                  }),

                  // Option 1 title
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "□  ",
                        bold: true,
                        size: 42,
                      }),
                      new TextRun({
                        text: "Option 1:- The Solar Project registered before Notification of the Gujarat Renewable Energy Policy-2023 and to avail Benefits of Gujarat Solar Power Policy -2021.",
                        bold: false,
                      }),
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 200, line: 240 },
                    indent: { left: 200 },
                  }),

                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "“M/s ",
                      }),
                      new TextRun({
                        text: productRegistration?.name,
                        underline: {},
                        bold: true,
                      }),
                      new TextRun({
                        text: " the first party under the agreement, hereby declares and agrees that it has voluntarily and willingly chosen to consider its RE Projects in accordance with the provisions of Gujarat Solar Policy -2021, as permitted under the clause 33 of Gujarat Renewable Energy Policy – 2023. First party accepts and acknowledges that the above is subject to certain conditions as specified in the Gujarat Renewable Energy Policy 2023 and the First Party shall be solely responsible for fulfilling the conditions there under including installation and commissioning of its RE projects within timelines stipulated under clause 33 of Gujarat Renewable Energy Policy – 2023 failing which it shall be governed by the provisions of Gujarat Renewable Energy Policy -2023 as applicable and it shall not raise any dispute in this regard against DGVCL before any forum/court. The first party further acknowledges and agrees to abide by applicable Hon’ble GERC regulations / orders/ decision from time to time.”",
                      }),
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 200, line: 360 },
                  }),

                  // Option 2 title
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "□  ",
                        bold: true,
                        size: 42,
                      }),
                      new TextRun({
                        text: "Option 2:- The Solar Project registered before Notification of the Gujarat Renewable Energy Policy -2023 and to avail the benefits of Gujarat RE Policy 2023.",
                        bold: false,
                      }),
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 200, line: 240 },
                    indent: { left: 200 },
                  }),

                  // Option 2 paragraph
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "“M/s ",
                      }),
                      new TextRun({
                        text: productRegistration?.name,
                        underline: {},
                        bold: true,
                      }),
                      new TextRun({
                        text: " the first party under the agreement, hereby declares and agrees that it has specially and voluntarily decided to consider its RE Projects under the provisions of Gujarat Renewable Energy Policy- 2023 read with applicable GERC Regulations / orders / decision in this regard. The first party acknowledges that the present agreements is on provisional arrangement basis till issuance of applicable regulations /order /decision published by GERC. Further, parties agree to abide by any Regulations/orders/decision of the Hon’ble GERC in relation to Gujarat Renewable Energy Policy 2023 and further agree to incorporate requisite modification and amendments in the agreement as per the same, if required. The first party must not dispute the applicability of the GERC order / Regulation and must make necessary modifications in the agreement as per the applicable GERC order and Regulation. The settlement will be done accordingly.”",
                      }),
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 200, line: 360 },
                  }),

                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "And the option opted once under this agreement will not be changed.",
                        bold: true,
                      }),
                    ],
                    spacing: { after: 200 },
                  }),
                ]
              : [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `In order to facilitate commissioning of the solar projects pursuant to notification of New the Gujarat Renewable energy Policy - 2023 effective from 04.10.2023, ${companyType}  has agreed to sign this agreement on Provisional basis with Consumer in terms of provisions of the Gujarat RE Policy-2023 and its incorporation in the Gujarat Electricity Regulatory Commission (Net Metering Rooftop Solar PV Grid Interactive Systems Regulations) Notification No. 5 of 2016 and its subsequent amendments subject to`,
                      }),
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 200, line: 360 },
                  }),

                  // Acknowledgment paragraph
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: '"M/s ',
                      }),
                      new TextRun({
                        text: `${productRegistration?.name}`,
                        underline: {},
                        bold: true,
                      }),
                      new TextRun({
                        text: " the first party under the agreement, hereby acknowledges that the present agreement has been entered into by both the parties, taking in to account the notification of new Gujarat RE policy -2023 and on provisional basis as an interim arrangement subject to change as per further regulation/order/decision of the Hon'ble GERC in relation to Gujarat Renewable Energy Policy 2023 and further agree to incorporate requisite modification and amendments in the agreement as per the same, if required. The first party must not dispute the applicability of the GERC order / Regulation and must make necessary modifications in the agreement as per the applicable GERC order and Regulation. The settlement will be done accordingly.\"",
                      }),
                    ],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 200, line: 360 },
                  }),
                ]),

            // AND WHEREAS 4
            new Paragraph({
              children: [
                new TextRun({
                  text: "AND WHEREAS",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The Distribution Licensee agrees to provide grid connectivity to the Consumer and injection of the electricity generated from his Solar PV System of capacity ",
                }),
                new TextRun({
                  text: `${inverterSize} `,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " KW (AC) into the power system of Distribution Licensee as per conditions of this agreement and in compliance with the applicable Policy / rules/ Regulations/ Codes (as amended from time to time) by the Consumer which includes-",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // Regulations list
            ...[
              "Government of Gujarat Renewable Energy Policy 2023.",
              "Central Electricity Authority (Measures relating to Safety and Electric Supply) Regulations, 2010.",
              "Central Electricity Authority (Technical Standards for Connectivity to the Grid) Regulations, 2007 as amended from time to time",
              "Central Electricity Authority (Installation and Operation of Meters) Regulation 2006.",
              "Gujarat Electricity Regulatory Commission (Electricity Supply Code & Related Matters) Regulations, 2015,",
              "Gujarat Electricity Regulatory Commission Distribution Code, 2004 and amendments thereto,",
              "Instruction, Directions and Circulars issued by Chief Electrical Inspector from time to time.",
              "CEA (Technical Standards for connectivity of the Distributed Generation) Regulations, 2013 as amended from time to time.",
              "Gujarat Electricity Regulatory Commission (Net Metering Rooftop Solar PV Grid Interactive Systems) Regulations, 2016 as amended from time to time.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index + 1}.  ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 200, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // Agreement statement
            new Paragraph({
              children: [
                new TextRun({
                  text: "Both the parties hereby agree as follows:",
                }),
              ],
              spacing: { after: 400 },
            }),

            // ELIGIBILITY SECTION
            new Paragraph({
              children: [
                new TextRun({
                  text: "1. Eligibility",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Consumer shall own the Solar PV System set up on its own premises or premises in his legal possession.",
              "Consumer needs to consume electricity in the same premises where Solar PV System is set up.",
              "Consumer has to meet the standards and conditions as specified in Gujarat Electricity Regulatory Commission Regulations and Central Electricity Authority Regulations and provisions of Government of Gujarat's Renewable Policy -2023 for being integrated into grid/distribution system.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `1.${index + 1}.  ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // TECHNICAL AND INTERCONNECTION REQUIREMENTS
            new Paragraph({
              children: [
                new TextRun({
                  text: "2. Technical and Interconnection Requirements",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Consumer agrees that his Solar PV System and Metering System will conform to the standards and requirements specified in the Policy, Regulations and Supply Code as amended from time to time.",
              "Consumer agrees that metering system(s) shall be installed at Solar PV System for recording the solar generation.",
              "Consumer agrees that he has installed or will install, prior to connection of Solar Photovoltaic System to Distribution Licensee's distribution system, an isolation device (both automatic and inbuilt within inverter and external manual relays) and agrees for the Distribution Licensee to have access to and operation of this, if required and for repair & maintenance of the distribution system.",
              "Consumer agrees that in case of a power outage on Discom's system, solar photovoltaic system will disconnect/isolate automatically and his plant will not inject power into Licensee's distribution system.",
              "All the equipment connected to the distribution system shall be compliant with relevant International (IEEE/IEC) or Indian Standards (BIS) and installations of electrical equipment must comply with Central Electricity Authority (Measures of Safety and Electricity Supply) Regulations, 2010 as amended from time to time.",
              "Consumer agrees that licensee will specify the interface/inter connection point and metering point.",
              "Consumer and licensee agree to comply with the relevant CEA Regulations in respect of operation and maintenance of the plant, drawing and diagrams, site responsibility schedule, harmonics, synchronization, voltage, frequency, flicker etc.",
              "In order to fulfill Distribution Licensee's obligation to maintain a safe and reliable distribution system, Consumer agrees that if it is determined by the Distribution Licensee that Consumer's Solar Photovoltaic System either causes damage to and/or produces adverse effects affecting other consumers or Distribution Licensee's assets, Consumer will have to disconnect Solar Photovoltaic System immediately from the distribution system upon direction from the Distribution Licensee and correct the problem to the satisfaction of distribution licensee at his own expense prior to reconnection.",
              "The consumer shall be solely responsible for any accident to human being/animals whatsoever (fatal/non-fatal/departmental/non-departmental) that may occur due to back feeding from the Solar Photovoltaic System when the grid supply is off if so decided by CEI. The distribution licensee reserves the right to disconnect the consumer's installation at any time in the event of such exigencies to prevent accident or damage to man and material.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `2.${index + 1}.  ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // CLEARANCES AND APPROVALS
            new Paragraph({
              children: [
                new TextRun({
                  text: "3. Clearances and Approvals",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "3.1 The Consumer shall obtain all the necessary statutory approvals and clearances (environmental and grid connection related) before connecting the photovoltaic system to the distribution system.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 240 },
              indent: { left: 200 },
            }),

            // ACCESS AND DISCONNECTION
            new Paragraph({
              children: [
                new TextRun({
                  text: "4. Access and Disconnection",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Distribution Licensee shall have access to metering equipment and disconnecting means of the Solar Photovoltaic System, both automatic and manual, at all times.",
              "In emergency or outage situation, where there is no access to the disconnecting means, both automatic and manual, such as a switch or breaker, Distribution Licensee may disconnect service to the premises of the Consumer.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `4.${index + 1}.  ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // LIABILITIES
            new Paragraph({
              children: [
                new TextRun({
                  text: "5. Liabilities",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Consumer shall indemnify Distribution Licensee for damages or adverse effects from his negligence or intentional misconduct in the connection and operation of Solar Photovoltaic System.",
              "Distribution Licensee shall not be liable for delivery or realization by the Consumer of any fiscal or other incentive provided by the Central/State Government.",
              "Distribution Licensee may consider the quantum of electricity generation from the Rooftop Solar PV System owned and operated by individual Residential, Group Housing Societies, and Residential Welfare Association consumers under net metering arrangement towards RPO compliance.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `5.${index + 1}.  ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // METERING
            new Paragraph({
              children: [
                new TextRun({
                  text: "6. Metering:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Metering arrangement shall be as per Central Electricity Authority (Installation And Operation of Meters) Regulations, 2006 as amended from time to time.",
              "Bi-directional meter shall be installed of same accuracy class as installed before Setting up of Rooftop Solar PV System.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `6.${index + 1}.  ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // COMMERCIAL SETTLEMENT
            new Paragraph({
              children: [
                new TextRun({
                  text: "7. Commercial Settlement",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "All the commercial settlements under this agreement shall be on provisional basis taking into account the notification of new Gujarat RE policy-2023 and as an interim arrangement subject to change as per further regulation/order/decision of GERC. Gujarat Electricity Regulatory Commission Regulations for Net Metering Rooftop Solar PV Grid Interactive Systems notification no.5 of 2016 and its subsequent amendments.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The commercial settlement will be as follows:",
                }),
              ],
              spacing: { after: 200, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "For Residential and common facility connections of Group Housing Societies/ Residential Welfare Association consumers.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "(i) In case of net import of energy by the consumer from distribution grid during billing cycle, the energy consumed from Distribution Licensee shall be billed as per applicable tariff to respective category of consumers as approved by the Commission from time to time. The energy generated by Rooftop Solar PV System shall be set off against units consumed (not against load/demand) and consumer shall pay demand charges, other charges, penalty etc as applicable to other consumers.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
              indent: { left: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "(ii) In case of net export of energy by the consumer to distribution grid during billing cycle, Distribution Licensee shall purchase surplus power, after giving set off against consumption during the billing period, at Rs. 2.25/Unit for the first 5 years from commissioning of project and thereafter for the remaining term of the project at 75% of the simple average of tariff discovered and contracted under competitive bidding process conducted by GUVNL for non-park based solar projects in the preceding 6-month period, i.e. either April to September or October to March as the case may be, from the commercial operation date (COD) of the project, subject to approval of Hon'ble GERC. Such surplus purchase shall be utilized for meeting RPO of Distribution Licensee. However, fixed / demand charges, other charges, penalty etc shall be payable as applicable to other consumers.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
              indent: { left: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Provided that in case the consumer is setting up additional solar rooftop capacity under the scheme over and above solar rooftop capacity set up prior to this scheme, surplus energy of entire solar rooftop capacity shall be purchased by Distribution Company at the rate of Rs. 2.25/Unit for the first 5 years from commissioning of project and thereafter for the remaining term of the project at 75% of the simple average of tariff discovered and contracted under competitive bidding process conducted by GUVNL for non-park based solar projects in the preceding 6- month period, i.e. either April to September or October to March as the case may be, from the commercial operation date (COD) of the project, treating earlier agreement as cancelled.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "In case of net injection, net amount receivable by consumer in a bill shall be credited in consumers account and will be adjusted against bill amount payable in subsequent months. However, at the end of year, if net amount receivable by consumer is more than Rs. 100/- and consumer requests for payment, the same may be paid. Such payment shall be made only once in a year based on year end position and request of consumer.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // CONNECTION COSTS
            new Paragraph({
              children: [
                new TextRun({
                  text: "8. Connection Costs:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The Consumer shall bear all costs related to setting up of Solar Photovoltaic System including metering and inter-connection. The Consumer agrees to pay the actual cost of modifications and upgrades to the service line, cost of up gradation of transformer to connect photovoltaic system to the grid in case it is required.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // INSPECTION, TEST, CALIBRATION AND MAINTENANCE
            new Paragraph({
              children: [
                new TextRun({
                  text: "9. Inspection, Test, Calibration and Maintenance prior to connection",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Before connecting, Consumer shall complete all inspections and tests finalized in consultation with the (Name of the Distribution license) and if required Gujarat Energy Transmission Corporation Limited (GETCO) to which his equipment is connected. Consumer shall make available to ${companyType} all drawings, specifications and test records of the project or generating station as the case may be.`,
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // RECORDS
            new Paragraph({
              children: [
                new TextRun({
                  text: "10. Records:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Each Party shall keep complete and accurate records and all other data required by each of them for the purposes of proper administration of this Agreement and the operation of the Solar PV System.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            // DISPUTE RESOLUTION
            new Paragraph({
              children: [
                new TextRun({
                  text: "11. Dispute Resolution:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "All disputes or differences between the Parties arising out of or in connection with this Agreement shall be first tried to be settled through mutual negotiation, promptly, equitably and in good faith.",

              "In the event that such differences or disputes between the Parties are not settled through mutual negotiations within sixty (60) days or mutually extended period, after such dispute arises, then for\n\n(a) Any dispute in billing pertaining to energy injection and billing amount, would be settled by the Consumer Grievance Redressal Forum and Electricity Ombudsman.\n\n(b) Any other issues pertaining to the Regulations and its interpretation; it shall be decided by the Gujarat Electricity Regulatory Commission following appropriate prescribed procedure.",
            ]
              .map((item, index) => {
                const parts = item.split("\n\n");

                return parts.map(
                  (line, i) =>
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: i === 0 ? `11.${index + 1}.  ${line}` : line,
                        }),
                      ],
                      alignment: AlignmentType.JUSTIFIED,
                      spacing: { after: 300, line: 240 },
                      indent: { left: 200 },
                    }),
                );
              })
              .flat(),

            // TERMINATION
            new Paragraph({
              children: [
                new TextRun({
                  text: "12. Termination",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "The Consumer can terminate agreement at any time by giving Distribution Licensee 90 days prior notice.",
              "Distribution Licensee has the right to terminate Agreement with 30 days prior written notice, if Consumer commits breach of any of the terms of this Agreement and does not remedy the breach within 30 days of receiving written notice from Distribution Licensee of the breach.",
              "Consumer shall upon termination of this Agreement, disconnect the Solar Photovoltaic System from Distribution Licensee's distribution system within one week to the satisfaction of Distribution Licensee.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `12.${index + 1}.  ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // COMMUNICATION
            new Paragraph({
              children: [
                new TextRun({
                  text: "Communication:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The names of the officials and their addresses, for the purpose of any communication in relation to the matters covered under this Agreement shall be as under:",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 100, line: 360 },
            }),
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: `In respect of the ${companyType}`,
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 500 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Chief Engineer",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 100 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text:
                                companyType === "MGVCL"
                                  ? "Madhya"
                                  : companyType === "PGVCL"
                                    ? "Paschim"
                                    : companyType === "UGVCL"
                                      ? "Uttar"
                                      : "Dakshin",
                            }),
                            new TextRun({
                              text: " Gujarat Vij Company,",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 200, line: 360 },
                        }),
                      ],
                    }),

                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "In respect of the Consumer",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,

                          spacing: { after: 500 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(                                )",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,

                          spacing: { after: 100 },
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "IN WITNESS WHEREOF, the Parties hereto have caused this Agreement to be executed by their authorized officers, and copies delivered to each Party, as of the day and year herein above stated.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { before: 400, after: 400, line: 360 },
            }),

            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: `FOR AND ON BEHALF OF ${companyType}`,
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 800 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Authorized Signatory",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 300 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "WITNESSES",
                              bold: true,
                            }),
                          ],
                          indent: { left: 200 },
                          spacing: { after: 200 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "1. ____________",
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 100 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(                                )",
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 300 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "2. ____________",
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 100 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(                                )",
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 100 },
                        }),
                      ],
                    }),

                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "FOR AND ON BEHALF OF THE PROJECT OWNER",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 400 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "________________",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 100 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Authorized Signatory",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 300 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "WITNESSES",
                              bold: true,
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 200 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "1. ____________",
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 100 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(                                )",
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 100 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "2. ____________",
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 100 },
                        }),

                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(                                )",
                            }),
                          ],
                          indent: { left: 200 },

                          spacing: { after: 100 },
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new TextRun("Page "),
                    new SimpleField("PAGE"),
                    new TextRun(" of "),
                    new SimpleField("NUMPAGES"),
                  ],
                  alignment: AlignmentType.RIGHT,
                }),
              ],
            }),
          },
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const fileName =
      companyType === "MGVCL"
        ? "Provisional_Agreement_MGVCL.docx"
        : companyType === "PGVCL"
          ? "Provisional_Agreement_PGVCL.docx"
          : companyType === "UGVCL"
            ? "Provisional_Agreement_UGVCL.docx"
            : "Provisional_Agreement_DGVCL.docx";
    saveAs(blob, fileName);
    toast.success("Provisional Agreement generated successfully!");
  } catch (error) {
    console.error("Error generating provisional agreement:", error);
    toast.error("Failed to generate provisional agreement");
  } finally {
    setGeneratingDoc(false);
  }
};

export const generateProvisionalAgreementTorrent = async (
  setGeneratingDoc: (value: boolean) => void,
  productRegistration: IproductRegistration,
  inverterSize: string,
) => {
  setGeneratingDoc(true);
  try {
    const doc = new Document({
      styles: {
        default: {
          document: {
            run: {
              font: "Calibri",
              size: 24,
            },
          },
        },
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
            },
          },
          children: [
            // Header
            new Paragraph({
              children: [
                new TextRun({
                  text: "Torrent Power Limited – Ahmedabad",
                  bold: true,
                  underline: {},
                  size: 28,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Annexure-IV",
                  bold: true,
                  size: 26,
                  underline: {},
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 },
            }),

            // Main Title
            new Paragraph({
              children: [
                new TextRun({
                  text: "Inter connection Agreement between Distribution Licensees and Solar Rooftop PV Project Owner",
                  bold: true,
                  size: 26,
                  underline: {},
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Net Metering/Gross-Metering Inter Connection Agreement",
                  bold: true,
                  size: 24,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 },
            }),

            // Agreement Opening
            new Paragraph({
              children: [
                new TextRun({
                  text: "This Agreement is made and entered into at ",
                }),
                new TextRun({
                  text: `Ahmedabad, Service No: `,
                  bold: true,
                }),
                new TextRun({
                  text: productRegistration?.consumerNumber,
                  underline: {},
                  bold: true,
                }),
                new TextRun({
                  text: " on this (date) __________ day of (Month) __________ Year __________ between the Consumer/Prosumer/Solar Power Systems (SPS) Developer under third party sale, by the name of ",
                }),
                new TextRun({
                  text: productRegistration?.name,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " having premises at (Address) ",
                }),
                new TextRun({
                  text: productRegistration?.address,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " as first party.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,

              spacing: { after: 400, line: 360 },
            }),

            // AND Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "AND",
                  bold: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }),

            // Torrent Power Details
            new Paragraph({
              children: [
                new TextRun({
                  text: "Torrent Power Limited",
                  bold: true,
                }),
                new TextRun({
                  text: ', Company registered under the Companies Act 1956/2013 and functioning as the "Distribution Licensee" under the Electricity Act 2003 having its Head Office at, ',
                }),
                new TextRun({
                  text: '"Samanyay", 600 Tapovan, Ambawadi, Ahmedabad- 380015',
                  bold: true,
                }),
                new TextRun({
                  text: " (hereinafter referred to as _ or Distribution Licensee which expression shall include its permitted assigns and Successors) a Party of the Second Part.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // AND WHEREAS 1
            new Paragraph({
              children: [
                new TextRun({
                  text: '"AND, WHEREAS ',
                }),
                new TextRun({
                  text: productRegistration?.name,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " (name of the consumer/Prosumer/SPS developer under third party Sale) desires to set-up such Solar Photovoltaic Rooftop System of ",
                }),
                new TextRun({
                  text: inverterSize,
                  bold: true,
                  underline: {},
                }),

                new TextRun({
                  text: " kW at Connected with ",
                }),
                new TextRun({
                  text: "Torrent Power Limited's",
                  bold: true,
                }),
                new TextRun({
                  text: ' grid at ____ Voltage level for his/her/its Own use or sale to consumers under third party sale to Distribution Licensee within the same Premises using the same point of supply".',
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // WHEREAS
            new Paragraph({
              children: [
                new TextRun({
                  text: "WHEREAS, the Gujarat Energy Development Agency (GEDA)/Gujarat Urja Vikas Nigam Limited(GUVNL)Ministry of New and Renewable Energy(MNRE) through letter- dated ",
                }),
                new TextRun({
                  text: `${formatToDDMMYYYY(
                    productRegistration?.registeredAt,
                  )}`,
                  bold: true,
                  underline: {},
                }),
                new TextRun({
                  text: " has registered for developing and setting up ",
                }),
                new TextRun({
                  text: inverterSize,
                  bold: true,
                  underline: {},
                }),

                new TextRun({
                  text: " kW own Rooftop Solar PV System for his/her/its own use, third party sale or sale to licensee under Gujarat Solar Power Policy – 2021/Gujarat Renewable Energy Policy 2023 read with Order No. 03 of 2020 dated 08.05.2020 and Orders dated 11.06.2021 & 06.07.2021 in Petition No. 1936 of 2021 at His's/her's/its's premises in legal possession or premises at rental basis including rooftop or Terrace.",
                }),
              ],
              spacing: { after: 400, line: 360 },
              alignment: AlignmentType.JUSTIFIED,
            }),

            // AND WHEREAS 2
            new Paragraph({
              children: [
                new TextRun({
                  text: "AND WHEREAS, the Distribution Licensee agrees to provide grid connectivity to the Consumer/Prosumer/SPS developer under third party sale for injection of the electricity Generated from his Rooftop Solar PV System of capacity ",
                }),
                new TextRun({
                  text: inverterSize,
                  bold: true,
                  underline: {},
                }),

                new TextRun({
                  text: " kilowatts (kW) into the power System of Distribution Licensee and as per conditions of this agreement and in Compliance with the applicable Policy / Rules / Regulations/ Codes (as amended from Time to time) by the Consumer/Prosumer/SPS developer under third party sale which includes-",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // Regulations List
            ...[
              "Government of Gujarat Solar Power Policy, 2021/Gujarat Renewable Energy Policy 2023",
              "Electricity (Rights of Consumers) Rules, 2020",
              "Central Electricity Authority (Measures relating to Safety and Electric Supply) Regulations, 2023.",
              "Central Electricity Authority (Technical Standards for Connectivity to the Grid) Regulations, 2007 as amended from time to time.",
              "Central Electricity Authority (Installation and Operation of Meters) Regulations, 2006 as amended from time to time.",
              "Gujarat Electricity Regulatory Commission (Electricity Supply Code & Related Matters) Regulations, 2015 as amended from time to time.",
              "Gujarat Electricity Regulatory Commission Distribution Code, 2004 and Amendments thereto,",
              "Instruction, Directions and Circulars issued by Chief Electrical Inspector from Time to time.",
              "CEA (Technical Standards for connectivity of the Distributed Generation) Regulations, 2013 as amended from time to time.",
              "Gujarat Electricity Regulatory Commission Notification No. 05 of 2016 Regulations for Net metering Rooftop Solar PV grid Interactive System and its amendments thereof.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index + 1}. ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 200, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // Agreement Statement
            new Paragraph({
              children: [
                new TextRun({
                  text: "Both the parties hereby agree as follows:",
                }),
              ],
              spacing: { after: 400 },
            }),

            // 1. Eligibility
            new Paragraph({
              children: [
                new TextRun({
                  text: "1. Eligibility",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Consumer/Prosumer/SPS developer under third party sale shall own the Rooftop Solar PV System set up on its own premises or premises in his legal possession, on lease or rental basis.",
              "Consumer needs to consume electricity generated from the Solar Power System set up in the same premises using same point of supply where Rooftop Solar PV System is set up.",
              "Consumer/Prosumer/SPS developer under third party sale shall ensure capacity of Rooftop Solar not to exceed than the limit specified in the Regulations. If it is violated then provisions of unauthorized use shall be applicable and consumer/prosumer/SPS developer shall be penalized as per the provisions of these Regulations.",
              "Consumer/Prosumer/SPS developer under third party sale has to meet the standards and conditions as specified in Gujarat Electricity Regulatory Commission Regulations and Central Electricity Authority Regulations and provisions of Government of Gujarat's Solar Power Policy, 2021/Gujarat Renewable Energy Policy 2023 for being integrated into grid/distribution system.",
              "Prosumer shall inject the electricity generated from Solar Power System into the grid to supply/sale to the Distribution Licensee.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `1.${index + 1} ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // 2. Technical and Interconnection Requirements
            new Paragraph({
              children: [
                new TextRun({
                  text: "2. Technical and Interconnection Requirements",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Consumer/Solar Project Developer under third party sale agrees that his/it Rooftop Solar PV System and Net Metering System will conform to the standards and requirements specified in the Policy, Regulations and Supply Code as amended from time to time.",
              "Prosumer agrees that his/it Rooftop Solar PV System and Gross Metering System will conform to the standards and requirements specified in the Electricity Rules, Policy, Supply Code and Regulations notified by the Commission as amended from time to time.",
              "Consumer/Solar Power System Developer under third party sale/Prosumer agrees that he/she/it has installed or will install, prior to connection of Rooftop Solar Photovoltaic System to Distribution Licensee's distribution system, an isolation device (both automatic and inbuilt within inverter and external manual relays) and agrees for the Distribution Licensee to have access to and operation of this, if required and for repair & maintenance of the distribution system.",
              "Consumer/ Solar Power System Developer under third party sale/Prosumer agrees that in case of non-availability of grid, Rooftop Solar Photovoltaic System will disconnect/isolate automatically and his/it plant will not inject power into the Licensee's distribution system.",
              "All the equipment connected to the distribution system shall be compliant with relevant International (IEEE/IEC) or Indian Standards (BIS) and installations of electrical equipment must comply with Central Electricity Authority (Measures of Safety and Electricity Supply) Regulations, 2010 as amended from time to time.",
              "Consumer/ Solar Power System Developer under third party sale/Prosumer agrees that licensee will specify the interface/inter connection point and metering point.",
              "Consumer/ Solar Power System Developer under third party sale/Prosumer and licensee agree to comply with the relevant CEA Regulations in respect of operation and maintenance of the plant, drawing and diagrams, site responsibility schedule, harmonics, synchronization, voltage, frequency, flicker etc.",
              "In order to fulfill Distribution Licensee's obligation to maintain a safe and reliable distribution system, Consumer/Prosumer/Solar Power System Developer under third party sale agrees that if it is determined by the Distribution Licensee that Consumer's/ Prosumer's/Solar Power System Developer's Rooftop Solar Photovoltaic System either causes damage to and/or produces adverse effects affecting other consumers or Distribution Licensee's assets, Consumer/Prosumer/SPS Developer will have to disconnect Rooftop Solar Photovoltaic System immediately from the distribution system upon direction from the Distribution Licensee and correct the problem to the satisfaction of Distribution Licensee at his own expense prior to reconnection.",
              "The consumer/Prosumer/Solar Power System Developer under third party sale shall be solely responsible for any accident to human being/animals whatsoever (fatal/non-fatal/departmental/non-departmental) that may occur due to back feeding from the Rooftop Solar plant when the grid supply is off if so decided by CEI.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `2.${index + 1} ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The Distribution Licensee reserves the right to disconnect the consumer's installation at any time in the event of such exigencies to prevent accident or damage to man and material.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),
            // 3. Clearances and Approvals
            new Paragraph({
              children: [
                new TextRun({
                  text: "3. Clearances and Approvals",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The Consumer/Prosumer/Solar Power System Developer under third party sale shall obtain all the necessary statutory approvals and clearances (environmental and grid connection related) before connecting the photovoltaic system to the distribution system.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // 4. Access and Disconnection
            new Paragraph({
              children: [
                new TextRun({
                  text: "4. Access and Disconnection",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Distribution Licensee shall have access to metering equipment, SPS and disconnecting means of the Rooftop Solar Photovoltaic Systems, both automatic and manual, at all times.",
              "In emergency or outage situation, where there is no access to the disconnecting means, both automatic and manual, such as a switch or breaker, Distribution Licensee may disconnect service to the premises of the Consumer.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `4.${index + 1} ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // 5. Liabilities
            new Paragraph({
              children: [
                new TextRun({
                  text: "5. Liabilities",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "Consumer/Prosumer/Solar Power System Developer under third party sale shall indemnify Distribution Licensee for damages or adverse effects from his negligence or intentional misconduct in the connection and operation of Rooftop Solar Photovoltaic System.",
              "Distribution Licensee shall not be liable for delivery or realization by the Consumer/Prosumer/Solar Power System Developer under third party sale of any fiscal or other incentive provided by the Central/State Government.",
              "Distribution Licensee may consider the quantum of electricity generation from the Rooftop Solar PV System set up by the project developer under third party sale or owned and operated by (i) Residential Consumers, (ii) Projects set up under Captive or Third Party, (iii) Projects set up under RPO or REC mechanism toward RPO compliance.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `5.${index + 1} ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // 6. Metering
            new Paragraph({
              children: [
                new TextRun({
                  text: "6. Metering:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Metering arrangement shall be as per Central Electricity Authority (Installation and Operation of Meters) Regulations, 2006 as amended from time to time.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "(a) In case of Residential Consumers, Bi-directional meter shall be installed of same accuracy class as installed before setting up of Rooftop Solar PV System.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 240 },
              indent: { left: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "(b) In case of Industrial, Commercial, Government Institutions and other consumers-",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 240 },
              indent: { left: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "(i) Projects set up under Captive or Third Party Sale:",
                  underline: {},
                }),
              ],
              spacing: { after: 200, line: 240 },
              indent: { left: 400 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Bi-directional meter shall be installed of same accuracy class as installed before setting up of Rooftop Solar PV System.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 240 },
              indent: { left: 600 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "(ii) Projects set up under RPO or REC Mechanism:",
                  underline: {},
                }),
              ],
              spacing: { after: 200, line: 240 },
              indent: { left: 400 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "ABT compliant meter having 15-minute integration shall be installed. Provided that separate generation meter shall be installed in all projects irrespective of arrangement i.e. net metering or gross metering.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 240 },
              indent: { left: 600 },
            }),

            // 7. Commercial Settlement
            new Paragraph({
              children: [
                new TextRun({
                  text: "7. Commercial Settlement",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'All commercial settlements under this agreement shall be as per GERC (Net Metering Rooftop Solar PV Grid Interactive Systems) Regulations, 2016 amended from time to time and Order No. 3 of 2020 dated 08.05.2020 and Suo-Motu Order No. 06 of 2020-dated 05.08.2020 of the Gujarat Electricity Regulatory Commission and any subsequent orders in this regard. In case of the energy injected from Solar Power System set up by the prosumer sale to Distribution Licensee under gross metering arrangement shall be purchased by the Distribution Licensee at the rate mechanism decided and specified by the Commission in its Order dated 08.08.2019 in Petition No. 1802 of 2019, pertaining to tariff for the Solar Power Projects set-up under "Policy for Development of Small Scale Distributed Solar Projects – 2019" as under:',
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: '"Applicable Tariff',
                  italics: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Tariff applicable shall be as per following mechanism:",
                  italics: true,
                }),
              ],
              spacing: { after: 200, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'The tariff contracted in the competitive bidding process conducted by GUVNL at which PPAs are signed for procurement of Solar Power from projects located outside Solar Park prevailing as on 31st March (computed based on simple average of such tariff discovered and contracted over six months ending on 31st March) of any given year with an addition of Rs. 0.20 per unit shall be the applicable tariff at which the PPAs shall be signed during the immediately succeeding period of April to September by Obligated Entities with these Solar Projects under these Regulations and similarly the contracted tariff prevailing as on 30th September of any given year (computed based on simple average of such tariff discovered and contracted over six months ending on 30th September) with an addition of Rs. 0.20 per unit shall be the applicable tariff for PPAs to be signed during the immediately succeeding period of October to March. The Rs. 0.20 per unit addition in tariff is allowed for Rs. 0.12 per unit for saving in transmission loss as power will be injected in distribution grid and Rs. 0.08 per unit is to compensate for expensive land cost, higher capital investment and maintenance cost due to small size of projects. The above mentioned tariff shall be applicable for a PPA term of 25 years from Commercial Operation Date of the Projects."',
                  italics: true,
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The commercial settlement will be as follows:",
                  italics: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            // 7.1 Gross Metering Arrangement
            new Paragraph({
              children: [
                new TextRun({
                  text: "7.1 Gross Metering Arrangement:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'The gross solar energy generated and registered in the Solar generation meter during billing cycle shall be compensated by the Distribution Licensee at the rate determined by the Commission in its Order 08.08.2019 in Petition No. 1802 of 2019 for Solar Power Projects set up under "Policy for Development of Small Scale Distributed Solar Projects – 2019".',
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // 7.2 For Residential and Government Consumers
            new Paragraph({
              children: [
                new TextRun({
                  text: "7.2 For Residential and Government Consumers:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "(i) In case of Residential Consumers, the Energy Accounting shall be carried out on Billing Cycle basis.",
              '(ii) "In case of net export (net injection) of energy by the consumer to distribution grid during billing cycle, the Distribution Licensee shall compensate for surplus power, after giving set off against consumption during the billing period, at following rates:',
              "(iii) In case of self-owned systems:",
              "(a) At Rs.2.25 / unit for the first 5 years from commissioning of project and thereafter for the remaining term of the project at 75% of the simple average of tariff discovered and contracted under competitive bidding process conducted by GUVNL for non-park based solar projects in the preceding six months period, i.e. either April to September or October to March as the case may be, from the Commercial Operation Date (COD) of the project.",
              "(b) In case of Third-Party Sale covered under Clause 9.2 -At 75% of the simple average of tariff discovered and contracted under competitive bidding process conducted by GUVNL for Non-park based solar projects in the preceding six months period, i.e., either April to September or October to March as the case may be, from the Commercial Operation Date (COD) of the project. The same shall remain fixed for the entire term of agreement. Such rates shall be declared by GUVNL on six monthly basis and shall be applicable under the connectivity agreement to be executed by Distribution Licensees.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: item,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 200, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // 7.3 For the projects set up under Captive use
            new Paragraph({
              children: [
                new TextRun({
                  text: "7.3 For the projects set up under Captive use:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "(i) In case of solar projects set up by HT / EHV consumers for captive use, the energy set-off shall be allowed between 07.00 hours to 18.00 hours of the same day meaning thereby, the generated solar energy during a day shall be consumed by HT or EHV consumer during 07.00 hours to 18.00 hours on the same day.",
              "(ii) In case of solar projects set up by LT demand-based consumers for captive use, the energy set-off shall be allowed between 07.00 hours to 18.00 hours basis of the same billing cycle meaning thereby, the generated solar energy during 7:00 hours to 18:00 hours billing cycle shall be consumed by the consumer during the specified period, of 7:00 hours to 18:00 hours in the same billing cycle.",
              "(iii) The energy accounting for all other LT consumers i.e., other than demand-based LT consumers shall be on billing cycle basis.",
              "(iv) The surplus energy, not consumed by the consumer during the above mentioned after setoff period shall be compensated by Distribution Licensees at following rates (SIC).",
              "(a) In case of MSME Manufacturing Enterprises - At Rs 2.25 / unit for first 5 years from commissioning of project and thereafter for the remaining term of the project at 75% of the simple average of tariff discovered and contracted under competitive bidding process conducted by GUVNL for non-park based solar projects in the preceding six months period, i.e., either April to September or October to March as the case may be, from the Commercial Operation Date (COD) of the project. The same shall remain fixed for the entire term of the agreement.",
              "(b) In case of other than MSME Manufacturing Enterprises-At 75% of the simple average of tariff discovered and contracted through competitive bidding process conducted by GUVNL for Non-park based solar projects in the preceding six months period, i.e., either April to September or October to March as the case may be, from the Commercial Operation Date (COD) of the project. The same shall remain fixed for the entire term of the agreement.",
              "(v) Excess drawl by consumer from the grid, if any, after giving set off shall be charged by the Distribution Licensee at the applicable tariff of the respective category of consumer as determined by the Commission from time to time.",
              "(vi) In case of projects set up for captive use, no cross-subsidy surcharge and additional surcharge shall be applicable.",
              "(vii) Banking charges of Rs.1.50/ unit shall be applicable on solar energy consumed in case of Demand based Consumers shall be applicable. In case of MSME units and other than Demand based Consumers, Banking Charges of Rs.1.10 per unit on Solar Energy consumed shall be applicable. Banking Charges shall not be applicable to government buildings.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: item,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 200, line: 240 },
                  indent:
                    index === 4 || index === 5 ? { left: 600 } : { left: 200 },
                }),
            ),

            // 7.4 For the projects set up under third party sale
            new Paragraph({
              children: [
                new TextRun({
                  text: "7.4 For the projects set up under third party sale:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "(i) In case of solar projects set up by HT / EHV consumers, the energy set-off shall be allowed between 07.00 hours to 18.00 hours of the same day meaning thereby, the generated solar energy during a day shall be consumed by HT or EHV consumer during 07.00 hours to 18.00 hours on the same day. The surplus energy after the specified period shall be purchased by Distribution licensee at rates specified under these Regulations.",
              "(ii) In case of solar projects set up by LT demand-based consumers, the energy setoff shall be allowed between 07.00 hours to 18.00 hours basis of the same billing cycle meaning thereby, the generated solar energy during 7:00 hours to 18:00 hours of a billing cycle shall be consumed by the consumer during the specified period of 7:00 hours to 18:00 hours in the same billing cycle. While in case of Solar Rooftop project set up by other than demand based LT consumers the energy generated shall be given set-off on billing cycle basis.",
              "(iii) The surplus energy injected into the Grid during the above mentioned setoff period shall be compensated by Distribution Licensees at 75% of the simple average of tariff discovered and contracted by GUVNL through competitive bidding process for non-park based solar projects in the preceding six months period, i.e., either April to September or October to March as the case may be, from the Commercial Operation Date (COD) of the project. The same shall remain fixed for the entire term of the agreement.",
              "(iv) Excess drawl of electricity by consumer from the grid, if any, after giving set off shall be charged by Distribution Licensee at applicable tariff of respective category of consumer as determined by the Commission from time to time.",
              "(v) No Cross Subsidy Surcharge and Additional Surcharge shall be applicable in case of captive consumption. But, in case of third party sale projects, both these charges shall be applicable. These surcharges shall be similar to normal open access consumers as determined by the Commission from time to time.",
              "(vi) Banking charges of Rs 1.50 / unit shall be applicable on solar energy consumed in case of Demand based Consumers shall be applicable. In case of MSME units and other than Demand based Consumers, Banking Charge of Rs.1.10 per unit on Solar Energy Consumed shall be applicable. Banking Charges shall not be applicable to government buildings.",
            ].map(
              (item) =>
                new Paragraph({
                  children: [new TextRun({ text: item })],
                  spacing: { after: 200, line: 240 },
                  alignment: AlignmentType.JUSTIFIED,
                  indent: { left: 200 },
                }),
            ),
            new Paragraph({
              children: [
                new TextRun({
                  text: "7.5 For the projects set up under REC mechanism:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            // 7.5 For the projects set up under REC mechanism
            ...[
              "(i) Developers may set up solar power projects under the REC mechanism in accordance with the administrative procedure regarding registration and accreditation, as decided by the Central Electricity Regulatory Commission (CERC), and as amended from time to time.",
              "(ii) In case of projects to be set up for captive/third party sale under REC mechanism, installation of solar projects up to sanctioned load/contract demand shall be allowed subject to the provision of Regulation 6.2 and other provisions of these Regulations.",
              "(iii) The energy accounting for the projects set up under REC mechanism shall be carried out on 15-minute time block basis.",
              "(iv) In case of projects set up for captive/third party sale under REC mechanism, surplus energy after giving set-off on 15 min time block basis, shall be compensated by Distribution Licensees at 65% of the simple average of tariff discovered and contracted by GUVNL through competitive bidding process for non-park based solar projects in the preceding six months period, i.e., either April to September or October to March as the case may be, from the Commercial Operation Date (COD) of the project. The same shall remain fixed for the entire term of the agreement.",
              "(v) Excess drawl by consumer from the grid, if any, after giving set off shall be charged by Distribution Licensee at applicable tariff of respective category of consumer as determined by the Commission from time to time.",
              "(vi) In case Distribution Licensee agrees to purchase the electricity component of power from a project under REC mechanism, the applicable tariff payable by Distribution Licensees shall be, 65% of the simple average of tariff discovered and contracted by GUVNL through competitive bidding process for non-Park based solar projects in the preceding six months period, i.e., either April to September or October to March as the case may be, from the date on which the PPA is executed. The same shall remain fixed for the entire term of the agreement.",
              "(vii) No banking charges shall be applicable.",
              "(viii) In case of Projects set up for third party sale, Cross Subsidy Surcharge and Additional Surcharge shall be applicable similar to normal open access consumers as determined by the Commission from time to time.",
            ].map(
              (item) =>
                new Paragraph({
                  children: [new TextRun({ text: item })],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 200, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // 7.6 For the projects set up for RPO compliance
            new Paragraph({
              children: [
                new TextRun({
                  text: "7.6 For the projects set up for RPO compliance:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "(i) Consumers shall be allowed to set up projects to fulfill their RPO requirements regardless of their contracted demand. For such projects the energy accounting shall be done on a 15 minute time block basis.",
              "(ii) The surplus solar energy purchased by Distribution Company from captive/third party solar projects shall be considered for fulfilling RPO of such Distribution Licensee.",
              "(iii) The surplus energy injected into the Grid shall be compensated by Distribution licensee at 75% of the simple average of tariff discovered and contracted by GUVNL through competitive bidding process for non-park based solar projects in the preceding six months period, i.e., either April to September or October to March as the case may be, from the Commercial Operation Date (COD) of the project. The same shall remain fixed for the entire term of the agreement.",
              "(iv) Excess drawl by consumer from the grid, if any, after giving set off shall be charged by Distribution Licensee at applicable tariff of respective category of consumer as determined by the Commission from time to time.",
              "(v) Cross Subsidy Surcharge and Additional Surcharge shall not be applicable in case of captive consumption. For third party sale projects, both the charges shall be applicable. These surcharges shall be similar to normal open access consumers as determined by the Commission from time to time.",
              "(vi) No banking charges shall be applicable.",
            ].map(
              (item) =>
                new Paragraph({
                  children: [new TextRun({ text: item })],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 200, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // 8. Connection Costs
            new Paragraph({
              children: [
                new TextRun({
                  text: "8. Connection Costs",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The Consumer/ Prosumer/Solar Power System Developer under third party sale shall bear all costs related to setting up of Rooftop Solar Photovoltaic System including metering and inter-connection. The Consumer/Prosumer/Solar Power System Developer under third party sale agrees to pay the actual cost of modifications and upgrades to the service line, cost of up gradation of transformer to connect photovoltaic system to the grid in case it is required.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // 9. Inspection, Test, Calibration and Maintenance
            new Paragraph({
              children: [
                new TextRun({
                  text: "9. Inspection, Test, Calibration and Maintenance prior to connection:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Before connecting, Consumer/ Prosumer/Solar Power System Developer under third party sale shall complete all inspections and tests finalized in consultation with the (Name of the Distribution license) and if required Gujarat Energy Transmission Corporation Limited (GETCO) to which his equipment is connected. Consumer/ Prosumer/Solar Power System Developer under third party sale shall make available to Distribution Licensee all drawings, specifications and test records of the project or generating station as the case may be.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // 10. Records
            new Paragraph({
              children: [
                new TextRun({
                  text: "10. Records:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Each Party shall keep complete and accurate records and all other data required by each of them for the purposes of proper administration of this Agreement and the operation of the Rooftop Solar PV System.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // 11. Dispute Resolution
            new Paragraph({
              children: [
                new TextRun({
                  text: "11. Dispute Resolution:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "All disputes or differences between the Parties arising out of or in connection with this Agreement shall be first tried to be settled through mutual negotiation, promptly, equitably and in good faith.",
              "In the event that such differences or dispute between the Parties is not settled through mutual negotiations within 60 days or mutually extended period, after such dispute arises, then for",
              "(a) any dispute in billing pertaining to energy injection and billing amount, it would be settled by the Consumer Grievance Redressal Forum and Electricity Ombudsman.",
              "(b) any other issues pertaining to the Regulations and its interpretation; it shall be decided by the Gujarat Electricity Regulatory Commission following appropriate prescribed procedure.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: index < 2 ? `11.${index + 1} ${item}` : item,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: index < 2 ? { left: 200 } : { left: 400 },
                }),
            ),

            // 12. Termination
            new Paragraph({
              children: [
                new TextRun({
                  text: "12. Termination:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            ...[
              "The Consumer/Prosumer/Solar Power System Developer under third party sale can terminate agreement at any time by giving 90 days prior notice to Distribution Licensee.",
              "Distribution Licensee shall have right to terminate Agreement with 30 days prior written notice, if Consumer/Prosumer/Solar Power System Developer under third party sale commits breach of any of the terms of this Agreement and does not remedy the breach.",
              "Consumer/Prosumer/Solar Power System Developer under third party sale shall upon termination of this Agreement, disconnect the Rooftop Solar Photovoltaic System from Distribution Licensee's distribution system within one week to the satisfaction of Distribution Licensee.",
            ].map(
              (item, index) =>
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `12.${index + 1} ${item}`,
                    }),
                  ],
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 300, line: 240 },
                  indent: { left: 200 },
                }),
            ),

            // Communication Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Communication:",
                  bold: true,
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "The names of the officials and their addresses, for the purpose of any communication in relation to the matters covered under this Agreement shall be as under:",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400, line: 360 },
            }),

            // Communication Table
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "In respect of the (Torrent Power Limited):",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 800 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Name: ____________________________",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 400 },
                        }),
                      ],
                      width: { size: 50, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "In respect of the Consumer/ Prosumer/ Solar Power System Developer under third party sale",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 300 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Name: ____________________________",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 400 },
                        }),
                      ],
                      width: { size: 50, type: WidthType.PERCENTAGE },
                    }),
                  ],
                }),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Authorized person by the Distribution Licensee …",
                }),
              ],
              spacing: { after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "IN WITNESS WHEREOF, the Parties hereto have caused this Agreement to be executed by their authorized officers, and copies delivered to each Party, as of the day and year herein above stated.",
                }),
              ],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { before: 400, after: 400, line: 360 },
            }),

            // Signature Table
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "FOR AND ON BEHALF OF Distribution Licensee",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 600 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Authorized Signatory",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 200 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "WITNESSES",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "1. Sign: ______________",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 100 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(Name -                                     )",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 100 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "2. Sign: ______________",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(Name -                                     )",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 100 },
                        }),
                      ],
                      width: { size: 50, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "FOR AND ON BEHALF OF THE PROJECT OWNER",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 600 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Authorized Signatory",
                            }),
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: { after: 200 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "WITNESSES",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "1. Sign: ______________",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 100 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(Name -                                     )",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 100 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "2. Sign: ______________",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "(Name -                                     )",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 100 },
                        }),
                      ],
                      width: { size: 50, type: WidthType.PERCENTAGE },
                    }),
                  ],
                }),
              ],
            }),

            // Bank Details Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Bank Details:",
                  bold: true,
                  size: 26,
                }),
              ],
              spacing: { before: 800, after: 400 },
            }),

            // Bank Details Table
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "1",
                            }),
                          ],
                        }),
                      ],
                      width: { size: 10, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Service No.",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                      width: { size: 30, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],

                      width: { size: 60, type: WidthType.PERCENTAGE },
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "2",
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Register Customer Name",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                // Bank Details Header
                new TableRow({
                  children: [
                    new TableCell({
                      rowSpan: 9,
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "3",
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      columnSpan: 2,
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Bank Details",
                              bold: true,
                              underline: {},
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                  ],
                }),

                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Account Type Saving / Current",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Bank Account No.",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Bank Account Holder Name",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "IFSC Code",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "MICR Code",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Bank Name",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Bank Branch",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Bank Address",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "4",
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Contact No. (Mobile no.)",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "5",
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "PAN No.",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "6",
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Email ID",
                            }),
                          ],
                          indent: { left: 100 },
                          spacing: { after: 200 },
                        }),
                      ],
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "" })],
                    }),
                  ],
                }),
              ],
            }),

            // Closing Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Date:",
                }),
              ],
              spacing: { before: 800, after: 500 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Sign. of Register Customer",
                }),
              ],
              alignment: AlignmentType.RIGHT,
              spacing: { after: 400 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "Attachment:",
                  bold: true,
                }),
                new TextRun({
                  text: "     1. Cancelled Cheque",
                  bold: true,
                }),
              ],
              spacing: { after: 100 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "                            2. Copy of PAN Card",
                  bold: true,
                }),
              ],
              spacing: { after: 400 },
            }),
          ],
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  alignment: AlignmentType.LEFT,
                  children: [
                    new SimpleField("PAGE"),
                    new TextRun({
                      text: " | Page",
                    }),
                  ],
                }),
              ],
            }),
          },
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "Provisional_Agreement_Torrent.docx");
    toast.success(
      "Torrent Power Provisional Agreement generated successfully!",
    );
  } catch (error) {
    console.error("Error generating Torrent provisional agreement:", error);
    toast.error("Failed to generate Torrent provisional agreement");
  } finally {
    setGeneratingDoc(false);
  }
};
